<template>
  <div v-if="loggedIn"
    id='profile-dropdown'
    class="dropdown is-right"
    :class="{'is-active': $store.state.profileDropDownOpen === true}"
  >
    <div @click.prevent="toggleProfileDropDown"
      class="dropdown-trigger"
      tabindex="0" role="button"
      @keypress.enter.prevent="toggleProfileDropDown"
    >
      <div
        class="navbar-profile" :class="{ navbarProfileLocation: isLocationId }"
        aria-haspopup="true"
        aria-controls="dropdown-menu1"
        title="User Info Menu"
      >
        <span class="is-size-6">{{user.firstName}} {{user.lastName}}</span>
        <span class="icon">
          <font-awesome-icon :icon="['fas', 'user-circle']" size="2x"/>
        </span>
      </div>
    </div>
    <div class="dropdown-menu profile-menu" id="dropdown-menu1">
      <div class="dropdown-item">
        <div class="columns">
          <div class="column is-3">
            <font-awesome-icon
              :icon="['fas', 'user-circle']"
              size="3x"
              style="color: black"
              title="User Info"
            />
          </div>
          <div class="column is-6">
            <div class="row has-text-left">
              {{ user.firstName }}
            </div>
            <div class="row has-text-left">
              {{ user.lastName }}
            </div>
          </div>
          <div class="column is-2" v-if= "hasModule('SYS:SEC:USRMGR:EDIT')">
            <a @click.prevent="edit(user.id)"
              tabindex="0" role="button"
              @keypress.enter.prevent="edit(user.id)"
            >
              <font-awesome-icon :icon="['fas', 'edit']" size="lg" title="Edit"/>
            </a>
          </div>
        </div>
      </div>
      <div class="dropdown-item">
        <div class="columns">
          <div class="column is-2">
            <font-awesome-icon :icon="['fas', 'envelope']" size="lg" title="Email Address"/>
          </div>
          <div class="column has-text-left is-10">
            {{ user.email }}
          </div>
        </div>
      </div>
      <div class="dropdown-item">
        <div class="columns">
          <div class="column is-2">
            <font-awesome-icon :icon="['fas', 'phone']" size="lg" title="Phone Number"/>
          </div>
          <div class="column has-text-left is-10">
            <span v-if="user.phone1">
              {{ formatPhone(user.phone1) }}
            </span>
            <span v-else>
              N/A
            </span>
          </div>
        </div>
      </div>
      <es-loader v-if="loading" size="2x" style="margin: 1rem 0"/>
      <template v-else>
        <div class="dropdown-item">
          <div class="columns">
            <div class="column is-2">
              <font-awesome-icon :icon="['fas', 'briefcase']" size="lg" title="Title"/>
            </div>
            <div class="column has-text-left is-10">
              <span v-if="user.jobClassificationCode">
                {{ mapTitle(user.jobClassificationCode) }}
              </span>
              <span v-else>
                N/A
              </span>
            </div>
          </div>
        </div>
        <div class="dropdown-divider">
        </div>
        <div class="dropdown-item">
          <div class="columns">
            <div class="column is-2">
              <font-awesome-icon :icon="['fas', 'door-open']" size="lg" title="Role(s)"/>
            </div>
            <div class="column has-text-left is-10">
              <div class="flex-box flex-item"
                v-for="role in allRoleIds().slice(0, 5)"
                :key="role.id"
              >
                {{ mapRole(role.id) }}
              </div>
              <div class="flex-box flex-item"
                v-show ="allRoleIds().length > maxResults"
              >
                <a @click="edit(user.id)"
                  tabindex="0" role="button"
                  @keypress.enter.prevent="edit(user.id)"
                >
                  View More
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="dropdown-divider">
        </div>
        <div class="dropdown-item">
          <div class="columns">
            <div class="column is-2">
              <font-awesome-icon :icon="['fas', 'map-marker-alt']" size="lg" title="Location Group(s)"/>
            </div>
            <div class="column has-text-left is-10">
              <div v-if="isAllGroups" class="flex-box flex-item">
                All Groups
              </div>
              <div v-else>
                <div class="flex-box flex-item"
                  v-for="groupId in allGroupIds.slice(0, 5)" :key="groupId"
                >
                  {{ mapGroup(groupId) }}
                </div>
                <div class="flex-box flex-item"
                  v-show ="allGroupIds.length > maxResults"
                >
                  <a @click="edit(user.id)"
                    tabindex="0" role="button"
                    @keypress.enter.prevent="edit(user.id)"
                  >
                    View More
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <div class="dropdown-divider">
      </div>
      <div class="dropdown-item">
        <div class="columns">
          <div class="column is-2">
            <font-awesome-icon
              :icon="['fas', 'sign-out']"
              size="lg"
              class="fa-flip-horizontal"
              title="Sign Out"
            />
          </div>
          <div class="column has-text-left is-10">
            <div class="row has-text-left">
              <router-link to="/logout">
                Sign Out
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import EsLoader from '@/components/EsLoader.vue';
import PermissionManager from '@/mixins/PermissionManager';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBriefcase,
  faDoorOpen,
  faEdit,
  faEnvelope,
  faMapMarkerAlt,
  faMobileAlt,
  faSignOut,
  faUserCircle,
  faPhone,
} from '@fortawesome/pro-solid-svg-icons';

library.add(
  faBriefcase,
  faDoorOpen,
  faEdit,
  faEnvelope,
  faMapMarkerAlt,
  faMobileAlt,
  faSignOut,
  faUserCircle,
  faPhone,
);

export default {
  mixins: [
    PermissionManager,
  ],
  components: {
    EsLoader,
  },
  data() {
    return {
      showPopper: false,
      maxResults: 5,
      results: [],
      loading: true,
    };
  },
  created() {
    this.onLoad();
  },
  computed: {
    ...mapGetters({
      properties: 'propertiesModule/properties',
      activeModule: 'appModules/getActiveModule',
    }),
    ...mapGetters('lookups', [
      'activeRoles',
      'activeTitles',
    ]),
    allGroupIds() {
      const ids = this.groupIdsArray.flat();
      const uniqueGroupIds = [...new Set(ids)];
      return uniqueGroupIds.sort();
    },
    groupIdsArray() {
      return this.user.locationPermissions
      .map(({ locationGroups }) => locationGroups
      .map(({ locationGroupId }) => locationGroupId));
    },
    isAllGroups() {
      return this.groupIdsArray.some(item => !item.length);
    },
    loggedIn() {
      return !(this.$store.state.user == null);
    },
    isLocationId(){
      if (this.activeModule.name === 'locationID') {
        return true;
      }
    },
    user() {
      return this.$store.state.user;
    },
  },
  methods: {
    ...mapActions([
      'toggleProfileDropDown',
    ]),
    ...mapActions('lookups', [
      'loadRoles',
      'loadTitles',
    ]),
    allRoleIds() {
      const u = this.user.locationPermissions;
      let result = u.map(({ role }) => role);
      return result;
    },
    edit(id) {
      this.toggleProfileDropDown();
      const module = this.activeModule.name;
      this.$router.push({ path: `/${module}/user/${id}` });
    },
    formatPhone(phone) {
      let numbers = phone.replace(/[^0-9]/g, '');
      if (numbers.length == 10) {
        return '(' + numbers.substr(0, 3) + ') ' + numbers.substr(3, 3) + '-' + numbers.substr(6, 4);
      }
    },
    mapGroup(code) {
      return this.results.find(result => result.id === code).label;
    },
    mapRole(code) {
      return this.activeRoles.find(r => r.id === code).name;
    },
    mapTitle(code) {
      return this.activeTitles.find(t => t.code === code).description;
    },
    async onLoad() {
      try {
        this.loading = true;
        const url = `/location-groups/lookup`;
        const res = await Promise.all([
          this.loadRoles(),
          this.loadTitles(),
          axios.get(url),
        ]);
        this.results = res[2].data;
      } catch (e) {
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .navbar-profile
     display: flex
     cursor: pointer
     &:hover
       color: #ccc !important
  .navbarProfileLocation
    &:hover
      color: #fff !important
  .icon
    justify-content: center
    align-items: center
    margin-left: .5em
  .profile-menu
    background: #fff
    -webkit-box-shadow: 0 4px 8px -3px #aaa
    box-shadow: 0 4px 8px -3px #aaa
    margin-top: 1.1em
    padding: 1em
    max-height: 64vh
    overflow-y: auto
  .flex-box
    display: flex
    flex-wrap: wrap
  .flex-item
    padding: 0.25em
  .dropdown-item .columns
    display: flex
    .column
      flex: 0 0 auto;
    .column.has-text-left
      flex: 1 1 auto
</style>
